import { useEffect, useMemo, useState } from 'react';
import { setMainHeading } from '../../../redux/modules/mainHeading/slice';
import { setShowList } from '../../../redux/modules/showList/slice';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Tabs, Tab } from '@mui/material';
import RadiologySettingsCards from './RadiologySettingsCards';
import LabDepartments from '../laboratory/settings/departments';
import LabSpecimenTypes from '../laboratory/settings/specimen-types';
import LabTestCatalog from '../laboratory/settings/test-catalog';
import LabTestPanels from '../laboratory/settings/test-panels';
import LabAnalyzers from '../laboratory/analyzers';
import LabEquipment from '../laboratory/equipment';
import AuditTrail from '../laboratory/audit';
import VoucherExport from '../laboratory/voucher-export';
import Branches from './branches';
import PermissionSettings from './permissions';
import Vouchers from '../vouchers';
import { usePermissions } from '../../../hooks/usePermissions';
import { isFeatureEnabled } from '../../../config/features';

export default function Settings() {
  const dispatch = useDispatch();
  const { userData } = useSelector(state => state.auth);
  const modules = useSelector(state => state.hospitalConfig?.modules);
  const { can } = usePermissions();
  // Voucher Submission tab: deployment feature flag AND the read permission.
  const voucherSubmission = useSelector(state => state.hospitalConfig?.features?.['lis.voucherSubmission'] === true);

  // Which modules this deployment runs — drives which settings areas appear.
  const lisOn = !!(modules?.lis?.enabled ?? modules?.lis);
  const risOn = !!(modules?.ris?.enabled ?? modules?.ris);

  // Laboratory sub-tabs. Voucher Submission appears only where the Guyana
  // voucher feature is enabled.
  const labSubTabs = useMemo(() => {
    const t = ['Departments', 'Specimen Types', 'Test Catalog', 'Test Panels', 'Analyzers', 'Equipment & Inventory'];
    if (voucherSubmission && can('labVoucherSubmission:read')) t.push('Voucher Submission');
    return t;
  }, [voucherSubmission, can]);

  const role = userData?.role;
  const isAdmin = role === 'admin';
  const isLabAdmin = ['admin', 'labAdmin'].includes(role);
  const isLabRole = ['admin', 'labAdmin', 'labDirector', 'labSupervisor', 'labTechnologist', 'labTechnician', 'phlebotomist', 'pathologist'].includes(role);

  // General (cross-cutting) sub-sections — not tied to any one module.
  const generalSubTabs = useMemo(() => {
    const t = [];
    if (isLabAdmin) t.push('Audit Trail');
    if (isAdmin) { t.push('Branches'); t.push('Permissions'); }
    if (isAdmin && isFeatureEnabled('billing.enabled')) t.push('Vouchers');
    return t;
  }, [isAdmin, isLabAdmin]);

  // Top-level tabs, gated by enabled modules + role. A disabled module's tab
  // never renders, so a lab-only site shows no Radiology tab and vice-versa.
  const mainTabs = useMemo(() => {
    const t = [];
    if (lisOn && isLabRole) t.push('Laboratory');
    if (risOn) t.push('Radiology');
    if (generalSubTabs.length) t.push('General');
    return t;
  }, [lisOn, risOn, isLabRole, generalSubTabs.length]);

  const [mainTab, setMainTab] = useState(0);
  const [labSubTab, setLabSubTab] = useState(0);
  const [generalSubTab, setGeneralSubTab] = useState(0);

  useEffect(() => {
    dispatch(setMainHeading("Settings"));
  }, [dispatch]);

  // Keep the selected tab valid if the available set changes (e.g. role load).
  useEffect(() => {
    if (mainTab > mainTabs.length - 1) setMainTab(0);
  }, [mainTabs.length, mainTab]);

  const activeMain = mainTabs[mainTab];

  return (
    <Box>
      <Tabs
        value={mainTab}
        onChange={(e, v) => setMainTab(v)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        {mainTabs.map((label) => (
          <Tab key={label} label={label} />
        ))}
      </Tabs>

      {activeMain === 'Laboratory' && (
        <Box>
          <Tabs
            value={labSubTab}
            onChange={(e, v) => { setLabSubTab(v); dispatch(setShowList(true)); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            {labSubTabs.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>

          {labSubTabs[labSubTab] === 'Departments' && <LabDepartments />}
          {labSubTabs[labSubTab] === 'Specimen Types' && <LabSpecimenTypes />}
          {labSubTabs[labSubTab] === 'Test Catalog' && <LabTestCatalog />}
          {labSubTabs[labSubTab] === 'Test Panels' && <LabTestPanels />}
          {labSubTabs[labSubTab] === 'Analyzers' && <LabAnalyzers />}
          {labSubTabs[labSubTab] === 'Equipment & Inventory' && <LabEquipment />}
          {labSubTabs[labSubTab] === 'Voucher Submission' && <VoucherExport />}
        </Box>
      )}

      {activeMain === 'Radiology' && <RadiologySettingsCards />}

      {activeMain === 'General' && (
        <Box>
          <Tabs
            value={generalSubTab}
            onChange={(e, v) => { setGeneralSubTab(v); dispatch(setShowList(true)); }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
          >
            {generalSubTabs.map((label) => (
              <Tab key={label} label={label} />
            ))}
          </Tabs>

          {generalSubTabs[generalSubTab] === 'Audit Trail' && <AuditTrail />}
          {generalSubTabs[generalSubTab] === 'Branches' && <Branches />}
          {generalSubTabs[generalSubTab] === 'Permissions' && <PermissionSettings />}
          {generalSubTabs[generalSubTab] === 'Vouchers' && <Vouchers />}
        </Box>
      )}
    </Box>
  );
}
